import type { DocumentNode } from 'graphql';
import type {
  UpdateBidStatusMutation,
  UpdateBidStatusMutationVariables,
  UpdateTransferOfferStatusMutation,
  UpdateTransferOfferStatusMutationVariables,
  GetVisibleBidsQuery,
  GetVisibleTransferOffersQuery,
  Player,
} from '../../../types/graphql-generated';

type UpdateBidFn = (options: {
  variables: UpdateBidStatusMutationVariables;
  refetchQueries?: Array<{ query: DocumentNode; variables?: Record<string, unknown> }>;
}) => Promise<{ data?: UpdateBidStatusMutation | null }>;

type UpdateTransferOfferFn = (options: {
  variables: UpdateTransferOfferStatusMutationVariables;
  refetchQueries?: Array<{ query: DocumentNode; variables?: Record<string, unknown> }>;
}) => Promise<{ data?: UpdateTransferOfferStatusMutation | null }>;

import { GET_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/activeMarketEntries';
import { GET_PAST_BIDS } from '../../../graphql/queries/pastBids';
import { GET_PAST_TRANSFER_OFFERS } from '../../../graphql/queries/pastTransferOffers';
import { GET_FANTASY_LEAGUE_MEMBERS } from '../../../graphql/queries/fantasyLeagueMembers';
import { GET_VISIBLE_BIDS } from '../../../graphql/queries/visibleBids';
import { GET_VISIBLE_TRANSFER_OFFERS } from '../../../graphql/queries/visibleTransferOffers';
import { GET_FANTASY_LEAGUE } from '../../../graphql/queries/fantasyLeague';
import { GET_FANTASY_LEAGUE_MEMBER_PLAYERS } from '../../../graphql/queries/fantasyLeagueMemberPlayers';
import { GET_TEAM_TAB_DATA } from '../../../graphql/queries/teamTabData';
import { GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/currentFantasyLeagueMemberActiveMarketEntries';
import { GET_FANTASY_LEAGUE_TABS_SHELL } from '../../../graphql/queries/fantasyLeagueTabsShell';
import { GET_MY_FANTASY_LEAGUES } from '../../../graphql/queries/myFantasyLeagues';

type VisibleBid = GetVisibleBidsQuery['visibleBids'][number];
type VisibleTransferOffer = GetVisibleTransferOffersQuery['visibleTransferOffers'][number];

export interface ReviewItemMutations {
  updateBidStatus: UpdateBidFn;
  updateTransferOfferStatus: UpdateTransferOfferFn;
}

export interface ReviewItem {
  getPlayer(): Player;
  getBidderName(): string;
  getAmount(): number;
  execute(
    newStatus: 'accepted' | 'rejected',
    leagueId: string,
    mutations: ReviewItemMutations
  ): Promise<string[]>;
}

class ReviewableBid implements ReviewItem {
  private bid: VisibleBid;

  constructor(bid: VisibleBid) {
    this.bid = bid;
  }

  getPlayer(): Player {
    return this.bid.marketEntry.player;
  }

  getBidderName(): string {
    return this.bid.bidder?.teamName || 'Futyclub';
  }

  getAmount(): number {
    return this.bid.amount;
  }

  async execute(
    newStatus: 'accepted' | 'rejected',
    leagueId: string,
    mutations: ReviewItemMutations
  ): Promise<string[]> {
    const refetchQueries = newStatus === 'accepted'
      ? [
          { query: GET_ACTIVE_MARKET_ENTRIES, variables: { fantasyLeagueId: leagueId } },
          { query: GET_FANTASY_LEAGUE_MEMBERS, variables: { id: leagueId } },
          { query: GET_PAST_BIDS, variables: { fantasyLeagueId: leagueId, limit: 20, offset: 0 } },
          { query: GET_VISIBLE_BIDS, variables: { fantasyLeagueId: leagueId } },
          { query: GET_FANTASY_LEAGUE, variables: { id: leagueId } },
          { query: GET_TEAM_TAB_DATA, variables: { fantasyLeagueId: leagueId } },
          { query: GET_FANTASY_LEAGUE_TABS_SHELL, variables: { fantasyLeagueId: leagueId } },
          { query: GET_MY_FANTASY_LEAGUES },
          { query: GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES, variables: { fantasyLeagueId: leagueId } },
          ...(this.bid.marketEntry.seller?.id
            ? [{ query: GET_FANTASY_LEAGUE_MEMBER_PLAYERS, variables: { fantasyLeagueMemberId: this.bid.marketEntry.seller.id } }]
            : []),
          ...(this.bid.bidder?.id
            ? [{ query: GET_FANTASY_LEAGUE_MEMBER_PLAYERS, variables: { fantasyLeagueMemberId: this.bid.bidder.id } }]
            : []),
        ]
      : [
          { query: GET_ACTIVE_MARKET_ENTRIES, variables: { fantasyLeagueId: leagueId } },
          { query: GET_PAST_BIDS, variables: { fantasyLeagueId: leagueId, limit: 20, offset: 0 } },
          { query: GET_VISIBLE_BIDS, variables: { fantasyLeagueId: leagueId } },
          { query: GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES, variables: { fantasyLeagueId: leagueId } },
        ];

    const { data } = await mutations.updateBidStatus({
      variables: { bidId: this.bid.id, newStatus },
      refetchQueries,
    });
    return data?.updateBidStatus?.errors ?? [];
  }
}

class ReviewableTransferOffer implements ReviewItem {
  private offer: VisibleTransferOffer;

  constructor(offer: VisibleTransferOffer) {
    this.offer = offer;
  }

  getPlayer(): Player {
    return this.offer.player;
  }

  getBidderName(): string {
    return this.offer.bidder.teamName ?? this.offer.bidder.id;
  }

  getAmount(): number {
    return this.offer.amount;
  }

  async execute(
    newStatus: 'accepted' | 'rejected',
    leagueId: string,
    mutations: ReviewItemMutations
  ): Promise<string[]> {
    const refetchQueries = newStatus === 'accepted'
      ? [
          { query: GET_FANTASY_LEAGUE_MEMBERS, variables: { id: leagueId } },
          { query: GET_FANTASY_LEAGUE, variables: { id: leagueId } },
          { query: GET_TEAM_TAB_DATA, variables: { fantasyLeagueId: leagueId } },
          { query: GET_FANTASY_LEAGUE_TABS_SHELL, variables: { fantasyLeagueId: leagueId } },
          { query: GET_MY_FANTASY_LEAGUES },
          { query: GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES, variables: { fantasyLeagueId: leagueId } },
          { query: GET_PAST_TRANSFER_OFFERS, variables: { fantasyLeagueId: leagueId, limit: 20, offset: 0 } },
          { query: GET_VISIBLE_TRANSFER_OFFERS, variables: { fantasyLeagueId: leagueId } },
          ...(this.offer.seller?.id
            ? [{ query: GET_FANTASY_LEAGUE_MEMBER_PLAYERS, variables: { fantasyLeagueMemberId: this.offer.seller.id } }]
            : []),
          ...(this.offer.bidder?.id
            ? [{ query: GET_FANTASY_LEAGUE_MEMBER_PLAYERS, variables: { fantasyLeagueMemberId: this.offer.bidder.id } }]
            : []),
        ]
      : [
          { query: GET_PAST_TRANSFER_OFFERS, variables: { fantasyLeagueId: leagueId, limit: 20, offset: 0 } },
          { query: GET_VISIBLE_TRANSFER_OFFERS, variables: { fantasyLeagueId: leagueId } },
        ];

    const { data } = await mutations.updateTransferOfferStatus({
      variables: { transferOfferId: this.offer.id, newStatus },
      refetchQueries,
    });
    return data?.updateTransferOfferStatus?.errors ?? [];
  }
}

export class ReviewItemFactory {
  static fromBid(bid: VisibleBid): ReviewItem {
    return new ReviewableBid(bid);
  }

  static fromTransferOffer(offer: VisibleTransferOffer): ReviewItem {
    return new ReviewableTransferOffer(offer);
  }
}
